import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import ReactEcharts from "echarts-for-react";
import { format, addDays, subDays, addMonths, subMonths, isSameMonth } from "date-fns";

import axios from "@app/lib/axios";

import CommonHorizontalBarChart from "@app/components/CommonHorizontalBarChart";
import CommonVerticalBarChart from "@app/components/CommonVerticalBarChart";
import AgGridReactEx from "@app/components/AgGridReactEx";

import FlyEngConstants from "@app/config/FlyEngConstants";

import flyengStyle from "../../components/style";

const appConnectChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: flyengStyle.seriesHomeAppConnectChart,
    },
    stacks: [{ stack: "base", showPercent: true, showSum: false, showSumTooltip: true }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    showSum: true,
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const appNewChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [{ name: "앱 설치 수", field: "newUser", stack: "base", type: "line", option: { itemStyle: { color: "#5ab1ef" } } }],
    },
    stacks: [{ stack: "base", showPercent: false, showSum: false, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const appAgeChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: [
            { name: "만2세", field: "age0Play", stack: "base0", showSum: false },
            { name: "만3세", field: "age1Play", stack: "base1", showSum: false },
            { name: "만4세", field: "age2Play", stack: "base2", showSum: false },
            { name: "만5세", field: "age3Play", stack: "base3", showSum: false },
        ],
    },
    stacks: [
        { stack: "base0", showSum: false },
        { stack: "base1", showSum: false },
        { stack: "base2", showSum: false },
        { stack: "base3", showSum: false },
    ],
    tooltipalign: "vertical",
    tooltipFormatter: (params, chart, test) => {
        var tooltipHtml = [""];
        var tooltipTitle = "";
        var total = 0;

        params.map((e) => {
            if (e.seriesName.indexOf("Sum-") < 0) {
                total += e.value;
            }
        });
        params.map((e) => {
            if (e.seriesName.indexOf("Sum-") < 0) {
                if (!tooltipTitle) tooltipTitle += e.name + `<br/>`;

                const stackIndex = 0;
                tooltipHtml[stackIndex] += e.marker + e.seriesName + " : " + (e.value ? e.value.toLocaleString() : "-");
                tooltipHtml[stackIndex] += total > 0 ? ` (` + Math.round((e.value / total) * 100) + "%)" : "";
                tooltipHtml[stackIndex] += "<br/>";
            }
        });
        var tooltip = tooltipTitle + '<div class="text-left">';
        tooltipHtml.forEach((t, index) => {
            tooltip += t;
        });
        tooltip += `<b>합계: ${total.toLocaleString()}</b>`;
        tooltip += `</div>`;

        return tooltip;
    },
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const contentChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: flyengStyle.seriesHomePlayChart2022,
    },
    stacks: [{ stack: "base", showSum: false, showSumTooltip: false }],
    showLegend: false,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "5%",
        left: "5%",
        right: "5%",
    },
};
const contentPlayChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: flyengStyle.seriesHomePlaySumChart2022,
    },
    stacks: [{ stack: "base", showPercent: false, showSum: false, showSumTooltip: false }],
    showLegend: false,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "5%",
        left: "5%",
        right: "5%",
    },
};
const executePathChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "건",
        series: FlyEngConstants.HOME.EXECUTE_PATH.map((gp) => {
            return { name: gp.name, field: `cnt${gp.id}`, stack: "base" };
        }),
    },
    stacks: [{ stack: "base", showPercent: true, showSum: false, showSumTooltip: true }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "5%",
        right: "5%",
    },
};
const libraryChartOption = {
    xAxis: {
        name: "건",
        series: [{ name: "", field: "value", stack: "base" }],
    },
    yAxis: { field: "name" },
    stacks: [{ stack: "base", showPercent: false, showSum: true, showSumTooltip: false }],
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "0%",
        left: "7%",
        right: "5%",
    },
    graph: {
        color: flyengStyle.seriesHomeColor2022.normal.slice(1),
    },
};
const parentChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "명",
        series: flyengStyle.seriesHomeParentChart,
    },
    stacks: [
        { stack: "base", showPercent: false, showSum: false, showSumTooltip: false },
        { stack: "base1", showPercent: false, showSum: false, showSumTooltip: false },
        { stack: "base2", showPercent: false, showSum: false, showSumTooltip: false },
    ],
    tooltipAlign: "vertical",
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "10%",
        right: "5%",
    },
};
const teacherChartOption = {
    xAxis: { field: "statDate" },
    yAxis: {
        name: "명",
        series: flyengStyle.seriesHomeTeacherChart,
    },
    stacks: [
        { stack: "base", showPercent: false, showSum: false, showSumTooltip: false },
        { stack: "base1", showPercent: false, showSum: false, showSumTooltip: false },
        { stack: "base2", showPercent: false, showSum: false, showSumTooltip: false },
    ],
    tooltipAlign: "vertical",
    showLegend: true,
    legend: {
        x: "right",
        y: "top",
    },
    sort: false,
    grid: {
        top: "15%",
        left: "10%",
        right: "5%",
    },
};
const StatDevice = ({ statAndroid, statIos }) => {
    const defaultColDef = {
        editable: false,
        sortable: true,
        flex: 1,
        minWidth: 60,
        filter: false,
        resizable: true,
        cellClass: "text-center",
        headerClass: "bg-dark text-light",
    };

    const listColumn = [
        {
            headerName: "OS 버전",
            field: "osVersion",
            minWidth: 80,
        },
        {
            headerName: "사용자 수",
            field: "cnt",
            minWidth: 70,
            cellRenderer: (param) => {
                return param.value ? param.value.toLocaleString() : "-";
            },
        },
        {
            headerName: "비율",
            field: "rate",
            minWidth: 70,
            sort: "desc",
            cellRenderer: (param) => {
                return param.value ? param.value.toFixed(1).toLocaleString() + "%" : "-";
            },
        },
    ];

    return (
        <div className="row">
            <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                <div className="panel">
                    <span className="h6 text-left">-AOS-</span>
                    {statAndroid && statAndroid.length > 0 ? (
                        <AgGridReactEx height={230} defaultColDef={defaultColDef} columnDefs={listColumn} rowData={statAndroid} />
                    ) : (
                        <div style={{ textAlign: "center", height: "230px", lineHeight: "230px" }} className="align-items-center">
                            통계 자료가 없습니다.
                        </div>
                    )}
                </div>
            </div>
            <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                <div className="panel">
                    <span className="h6">-iOS-</span>
                    {statIos && statIos.length > 0 ? (
                        <AgGridReactEx height={230} defaultColDef={defaultColDef} columnDefs={listColumn} rowData={statIos} />
                    ) : (
                        <div style={{ textAlign: "center", height: "230px", lineHeight: "230px" }} className="align-items-center">
                            통계 자료가 없습니다.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
const StatCategory = ({ libCategories, statAll }) => {
    const defaultColDef = {
        editable: false,
        sortable: true,
        flex: 1,
        minWidth: 60,
        filter: false,
        resizable: true,
        cellClass: "text-center",
        headerClass: "bg-dark text-light",
    };

    const listColumn = [
        {
            headerName: "",
            field: "row",
            minWidth: 100,
            maxWidth: 100,
            pinned: "left",
        },
    ];
    const stat = [];

    if (libCategories) {
        stat.push({ row: "순 실행 수" });
        stat.push({ row: "누적 실행 수" });
        libCategories.forEach((c) => {
            const fieldId = `playCountCat_${c.id}`;
            listColumn.push({
                headerName: c.name,
                field: fieldId,
                minWidth: 70,
                cellRenderer: (param) => {
                    return param.value ? param.value.toLocaleString() : "-";
                },
            });
            let s = statAll ? statAll.find((s) => s.categoryId == c.id) : null;
            stat[0][fieldId] = s ? s.playCount : 0;
            stat[1][fieldId] = s ? s.playCountSum : 0;
        });
    }

    return (
        <>
            {stat && stat.length > 0 ? (
                <AgGridReactEx height={115} defaultColDef={defaultColDef} columnDefs={listColumn} rowData={stat} />
            ) : (
                <div style={{ textAlign: "center", height: "115px", lineHeight: "115px" }} className="align-items-center">
                    통계 자료가 없습니다.
                </div>
            )}
        </>
    );
};
const App = forwardRef(({ statPeriod, libCategories }, ref) => {
    const [statApp, setStatApp] = useState(false);
    const [statAppAge, setStatAppAge] = useState(false);
    const [statContent, setStatContent] = useState(false);
    const [statNewUserOs, setStatNewUserOs] = useState(false);
    const [statDevice, setStatDevice] = useState(false);
    const [statDeviceAndroid, setStatDeviceAndroid] = useState(false);
    const [statDeviceIos, setStatDeviceIos] = useState(false);
    const [statPath, setStatPath] = useState(false);
    const [path, setPath] = useState(
        FlyEngConstants.HOME.EXECUTE_PATH.map((gp) => {
            return { name: gp.name, field: `cnt${gp.id}`, stack: "base" };
        })
    );
    const [statLibrary, setStatLibrary] = useState(false);
    const [statParent, setStatParent] = useState(false);
    const [statCategory, setStatCategory] = useState(false);

    const initStat = () => {
        setStatApp(false);
        setStatAppAge(false);
        setStatContent(false);
        setStatNewUserOs(false);
        setStatDevice(false);
        setStatDeviceAndroid(false);
        setStatDeviceIos(false);
        setStatPath(false);
        setPath(false);
        setStatLibrary(false);
        setStatParent(false);
        setStatCategory(false);
    };

    const appNewUserOsChartContent = {
        tooltip: {
            trigger: "item",
            formatter: (param) => {
                return param.data ? param.data.name + " : " + param.data.value.toLocaleString() + " (" + Math.ceil(param.percent) + "%)" : "";
            },
        },
        series: [
            {
                type: "pie",
                data: statNewUserOs,
                radius: ["0%", "95%"],
                animation: false,
                center: ["50%", "50%"],
                labelLine: {
                    show: false,
                },
                label: {
                    show: false,
                    position: "center",
                },
                color: ["#60d7c5", "#3861a5"],
            },
        ],
        legend: {
            x: "right",
            y: "top",
        },
        sort: false,
        grid: {
            top: "15%",
            left: "5%",
            right: "5%",
        },
    };

    useImperativeHandle(ref, () => ({
        getExcelData(callback) {
            const excelDataLibrary = [];
            if (statLibrary) {
                excelDataLibrary.push(
                    ["순 실행 수"].concat(
                        libCategories.map((lc) => {
                            const s = statCategory.find((s) => s.categoryId == lc.id);
                            return s ? s.playCount : 0;
                        })
                    )
                );
                excelDataLibrary.push(
                    ["누적 실행 수"].concat(
                        libCategories.map((lc) => {
                            const s = statCategory.find((s) => s.categoryId == lc.id);
                            return s ? s.playCountSum : 0;
                        })
                    )
                );
            }
            const excelData = {
                fileName: "플라잉홈_앱통계",
                sheets: [
                    { name: "접속", title: "접속 수 통계", columnTitle: ["", "앱", "웹"], data: statApp ? statApp.map((s) => [s.statDate, s.connectApp, s.connectWeb]) : [] },
                    { name: "설치", title: "앱 설치 수 통계", columnTitle: ["", "안드로이드", "IOS"], data: statApp ? statApp.map((s) => [s.statDate, s.newUserAndroid, s.newUserIos]) : [] },
                    { name: "OS", title: "OS별 사용자 통계", columnTitle: ["OS", "OS 버전", "사용자 수"], data: statDevice ? statDevice.map((s) => [s.os, s.osVersion, s.cnt]) : [] },
                    {
                        name: "연령",
                        title: "연령별 사용량 통계",
                        columnTitle: ["", "만2세", "만3세", "만4세", "만5세"],
                        data: statAppAge ? statAppAge.map((s) => [s.statDate, s.age0Play, s.age1Play, s.age2Play, s.age3Play]) : [],
                    },
                    {
                        name: "콘텐츠(순)",
                        title: "콘텐츠 순 실행 통계",
                        columnTitle: ["", "전체"],
                        data: statContent ? statContent.map((s) => [s.statDate, s.playCount]) : [],
                    },
                    {
                        name: "콘텐츠(누적)",
                        title: "콘텐츠 누적 실행 통계",
                        columnTitle: ["", "전체"],
                        data: statContent ? statContent.map((s) => [s.statDate, s.playCountSum]) : [],
                    },
                    {
                        name: "카테고리",
                        title: "카테고리별 콘텐츠 실행 수",
                        columnTitle: [""].concat(libCategories ? libCategories.map((lc) => lc.name) : []),
                        data: excelDataLibrary,
                    },
                    {
                        name: "메뉴",
                        title: "메뉴별 사용 통계",
                        columnTitle: ["", "경로없음", "금주의 학슴", "도서관", "보관함"],
                        data: statPath ? statPath.map((s) => [s.statDate, s["cnt-1"], s["cnt0"], s["cnt1"], s["cnt3"]]) : [],
                    },
                    {
                        name: "도서관",
                        title: "도서관 사용 통계",
                        columnTitle: statLibrary ? statLibrary.map((lc) => lc.name) : [],
                        data: statLibrary ? [statLibrary.map((s) => s.value)] : [],
                    },
                    {
                        name: "학부모",
                        title: "학부모/교사 가입 통계",
                        columnTitle: ["", "계약 원아 총 수", "회원 가입 총 수", "계약 클래스 총 수", "교사 가입 총 수"],
                        data: statParent ? statParent.map((s) => [s.statDate, s.class_n_child, s.parent_join, s.class_n, s.teacher_join]) : [],
                    },
                ],
            };
            callback(null, excelData);
        },
    }));

    const getStat = () => {
        initStat();
        axios({
            method: "POST",
            url: `/api/flyeng_stat_2022/home/app`,
            data: {
                startDate: statPeriod.startDate,
                endDate: statPeriod.endDate,
                limit: 5,
                periodType: statPeriod.type,
            },
        })
            .then((result) => {
                if (result.data.code === 200) {
                    const stat = result.data.data;
                    let statApp = [];
                    let statAppAge = [];
                    let statDevice = [];
                    let statDeviceAndroid = null;
                    let statDeviceIos = null;
                    let statContent = [];
                    let statPath = [];
                    let statPathTemp = [];
                    let statCategory = [];
                    let statLibrary = [];
                    let statParent = [];
                    let statNewUserOs = [
                        { id: 0, name: "Android", value: 0 },
                        { id: 1, name: "iOS", value: 0 },
                    ];

                    let pathNullExist = false;
                    if (stat.path && stat.path.length > 0) {
                        stat.path.forEach((s) => {
                            const group = FlyEngConstants.HOME.EXECUTE_PATH.find((ep) => s.path.indexOf(ep.groupPath) === 0);
                            if (group) {
                                const cntPath = `cnt${group.id}`;
                                const path = statPathTemp.find((sp) => sp.statDate == s.statDate);
                                if (path) {
                                    path[cntPath] = (path[cntPath] || 0) + s.cnt;
                                } else {
                                    const pathNew = { statDate: s.statDate };
                                    pathNew[cntPath] = s.cnt;
                                    statPathTemp.push(pathNew);
                                }
                                if (group.id < 0) {
                                    pathNullExist = true;
                                }
                            }
                        });
                    }
                    if (pathNullExist) {
                        const pathNew = path.shift();
                        setPath(pathNew);
                    }

                    let start = new Date(statPeriod.startDate);
                    let day = new Date(statPeriod.startDate);
                    for (let i = 0; i < 99; i++) {
                        const statDate = format(day, statPeriod.type === "year" ? "yyyy-MM" : "yyyy-MM-dd");
                        const statDateStr = format(day, statPeriod.type === "year" ? "yyyy-MM" : "MM-dd");
                        const sApp = stat.app.find((s) => s.statDate === statDate);
                        const sAge = stat.appAge.find((s) => s.statDate === statDate);
                        const sContents = stat.content.filter((s) => s.statDate === statDate);
                        const sPath = statPathTemp.find((s) => s.statDate === statDate);
                        const sParent = stat.parent.find((s) => s.statDate === statDate);
                        const sCategories = stat.statCategory.filter((s) => s.statDate === statDate);

                        if (sApp) {
                            sApp.newUser = (sApp.newUserAndroid || 0) + (sApp.newUserIos || 0);
                            statNewUserOs[0].value += sApp.newUserAndroid || 0;
                            statNewUserOs[1].value += sApp.newUserIos || 0;
                        }

                        const sContent = { statDate: statDateStr, playCount: 0, playCountSum: 0 };
                        sContent.playCount = sContents.reduce((acc, cur, idx) => {
                            return (acc += cur.playCount);
                        }, 0);
                        sContent.playCountSum = sContents.reduce((acc, cur, idx) => {
                            return (acc += cur.playCountSum);
                        }, 0);
                        statApp.push({ ...sApp, statDate: statDateStr });
                        statAppAge.push({ ...sAge, statDate: statDateStr });
                        statContent.push(sContent);
                        statPath.push({ ...sPath, statDate: statDateStr });
                        if (sParent) {
                            statParent.push({ ...sParent, statDate: statDateStr });
                        } else {
                            const yesterday = statPeriod.type === "year" ? subMonths(day, 1) : subDays(day, 1);
                            const statDateStrYesterday = format(yesterday, statPeriod.type === "year" ? "yyyy-MM" : "MM-dd");
                            const spYesterday = statParent.find((sp) => sp.statDate === statDateStrYesterday);

                            statParent.push({ ...spYesterday, statDate: statDateStr });
                        }
                        if (sCategories && sCategories.length > 0) {
                            sCategories.forEach((s) => {
                                const c = statCategory.find((c) => s.categoryId === c.categoryId);
                                if (c) {
                                    c.playCount += s.playCount;
                                    c.playCountSum += s.playCountSum;
                                } else {
                                    statCategory.push({ categoryId: s.categoryId, playCount: s.playCount, playCountSum: s.playCountSum });
                                }
                            });
                        }

                        if (statPeriod.type === "week") {
                            day = addDays(day, 1);
                            if (i >= 6) break;
                        } else if (statPeriod.type === "month") {
                            day = addDays(day, 1);
                            if (!isSameMonth(day, start)) break;
                        } else {
                            // year
                            day = addMonths(day, 1);
                            if (i > 11) break;
                        }
                    }
                    if (stat.device && stat.device.length > 0) {
                        stat.device.forEach((s) => {
                            const versionSplit = s.osVersion.split(".");
                            let osVersion = s.osVersion;
                            if (versionSplit && versionSplit.length > 2) {
                                osVersion = `${versionSplit[0]}.${versionSplit[1]}`;
                            }

                            const stat = statDevice.find((si) => si.osVersion == osVersion);
                            if (stat) {
                                stat.cnt += s.cnt;
                            } else {
                                statDevice.push({ os: s.os, osVersion: osVersion, cnt: s.cnt });
                            }
                        });
                    }
                    statDeviceAndroid = statDevice.filter((d) => d.os == "android");
                    if (statDeviceAndroid.length > 0) {
                        const total = statDeviceAndroid.reduce((ac, s) => ac + (s.cnt ? s.cnt : 0), 0);
                        statDeviceAndroid.forEach((s) => (s.rate = (s.cnt / total) * 100));
                    }
                    statDeviceIos = statDevice.filter((d) => d.os == "ios");
                    if (statDeviceIos.length > 0) {
                        const total = statDeviceIos.reduce((ac, s) => ac + (s.cnt ? s.cnt : 0), 0);
                        statDeviceIos.forEach((s) => (s.rate = (s.cnt / total) * 100));
                    }

                    libCategories.forEach((c) => {
                        if (c.id > 0) {
                            // 도서관 카테고리 추출 : 교실영어(0) 제외
                            statLibrary.push({ ...c, value: 0 });
                        }
                        if (stat.library && stat.library.length > 0) {
                            stat.library.forEach((s) => {
                                const lib = statLibrary.find((l) => l.id == s.categoryId);
                                if (lib) {
                                    lib.value = s.cnt;
                                }
                            });
                        }
                    });

                    setStatCategory(statCategory);
                    setStatApp(statApp);
                    setStatAppAge(statAppAge);
                    setStatNewUserOs(statNewUserOs);
                    setStatDeviceAndroid(statDeviceAndroid);
                    setStatDeviceIos(statDeviceIos);
                    setStatContent(statContent);
                    setStatPath(statPath);
                    setStatLibrary(statLibrary);
                    setStatParent(statParent);
                    setStatDevice(statDevice);
                } else {
                    alert(result.data && result.data.msg ? result.data.msg : "통계 정보를 가져오는데 실패하였습니다.\n지속 발생시 관리자에게 문의해주세요.");
                }
            })
            .catch((error) => {
                console.log(error);
                console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
            });
    };
    useEffect(() => {
        getStat();
        return () => {};
    }, [statPeriod]);

    return (
        <div className="w-100">
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">접속 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statApp} chartOption={appConnectChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">설치 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statApp} chartOption={appNewChartOption} />
                        </div>
                    </div>
                </div>
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">OS별 설치 비율</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <ReactEcharts theme="macarons" style={{ width: "100%", height: "100%" }} option={appNewUserOsChartContent} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">OS 버전별 사용자 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <StatDevice statAndroid={statDeviceAndroid} statIos={statDeviceIos} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">연령별 사용량</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statAppAge} chartOption={appAgeChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 순 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={contentChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">콘텐츠 누적 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statContent} chartOption={contentPlayChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">카테고리별 콘텐츠 실행 수</h4>
                        </div>
                        <div className="card-body" style={{ height: "150px" }}>
                            <StatCategory libCategories={libCategories} statAll={statCategory} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">메뉴별 사용 현황</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statPath} chartOption={executePathChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">도서관 사용 현황</h4>
                        </div>
                        <div className="card-body" style={{ height: `${statLibrary && statLibrary.length > 3 ? statLibrary.length * 40 : 150}px` }}>
                            <CommonVerticalBarChart chartData={statLibrary} chartOption={libraryChartOption} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">학부모 가입 현황</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statParent} chartOption={parentChartOption} />
                        </div>
                    </div>
                </div>
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                    <div className="card">
                        <div className="card-header d-flex" style={{ padding: "5px 20px" }}>
                            <h4 className="card-header-title">교사 가입 현황</h4>
                        </div>
                        <div className="card-body" style={{ height: "300px" }}>
                            <CommonHorizontalBarChart chartData={statParent} chartOption={teacherChartOption} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default App;
